
import { Link } from 'react-router-dom';
import Logo from '../base/Logo';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Premium Services",
      links: [
        { name: "Executive Fleet", href: "/fleet" },
        { name: "Corporate Solutions", href: "/corporate" },
        { name: "Long-term Rentals", href: "/long-term" },
        { name: "Chauffeur Services", href: "/chauffeur" },
        { name: "Airport Transfers", href: "/airport" }
      ]
    },
    {
      title: "Client Support",
      links: [
        { name: "24/7 Concierge", href: "/support" },
        { name: "Booking Management", href: "/manage" },
        { name: "Insurance Coverage", href: "/insurance" },
        { name: "Roadside Assistance", href: "/roadside" },
        { name: "FAQ & Policies", href: "/faq" }
      ]
    },
    {
      title: "Corporate",
      links: [
        { name: "About Car2Go", href: "/about" },
        { name: "Leadership Team", href: "/leadership" },
        { name: "Investor Relations", href: "/investors" },
        { name: "Sustainability", href: "/sustainability" },
        { name: "Press Center", href: "/press" }
      ]
    },
    {
      title: "Connect",
      links: [
        { name: "Contact Us", href: "/contact" },
        { name: "Locations", href: "/locations" },
        { name: "Partnerships", href: "/partnerships" },
        { name: "Careers", href: "/careers" },
        { name: "Newsletter", href: "/newsletter" }
      ]
    }
  ];

  const socialLinks = [
    { icon: "ri-linkedin-fill", href: "#", label: "LinkedIn" },
    { icon: "ri-twitter-x-fill", href: "#", label: "Twitter" },
    { icon: "ri-facebook-fill", href: "#", label: "Facebook" },
    { icon: "ri-instagram-line", href: "#", label: "Instagram" },
    { icon: "ri-youtube-fill", href: "#", label: "YouTube" }
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-8 py-16">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          {/* Brand Section */}
          <div className="lg:col-span-4">
            <div className="mb-8">
              {/* Logo */}
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mr-4">
                  <i className="ri-car-line text-white text-2xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>Car2Go</div>
                  <div className="text-xs text-blue-400 tracking-widest uppercase">Premium Mobility</div>
                </div>
              </div>
              
              <p className="text-gray-300 leading-relaxed mb-6 font-light">
                Established in 2021, Car2Go is New Jersey's trusted car rental company serving professionals and organizations across the region. 
                Experience reliable service and quality vehicles for all your transportation needs.
              </p>

              {/* Contact Info */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center text-gray-300">
                  <i className="ri-phone-line text-blue-400 mr-3 w-5"></i>
                  <span className="font-medium">+1 (732) 900-3478</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <i className="ri-mail-line text-blue-400 mr-3 w-5"></i>
                  <span className="font-medium">info@cartogo.co</span>
                </div>
                <div className="flex items-start text-gray-300">
                  <i className="ri-map-pin-line text-blue-400 mr-3 w-5 mt-0.5"></i>
                  <span className="font-medium">28 Hollis Rd<br />East Brunswick, NJ 08816<br />United States</span>
                </div>
              </div>

              {/* Awards */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                <div className="flex items-center mb-3">
                  <i className="ri-award-line text-amber-400 mr-2 text-lg"></i>
                  <span className="text-sm font-semibold text-amber-400 tracking-wide uppercase">Industry Recognition</span>
                </div>
                <div className="text-sm text-gray-300 space-y-1">
                  <div>• New Jersey Licensed Car Rental Provider</div>
                  <div>• Professional Fleet Management Standards</div>
                  <div>• Customer Service Excellence Award</div>
                </div>
              </div>
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {footerSections.map((section, index) => (
                <div key={index}>
                  <h4 className="text-lg font-bold mb-6 text-white">{section.title}</h4>
                  <ul className="space-y-3">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          to={link.href}
                          className="text-gray-300 hover:text-blue-400 transition-colors duration-200 font-light"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Newsletter Subscription */}
        <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-3xl p-8 mb-12 border border-blue-800/30">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-3">Stay Connected</h3>
              <p className="text-gray-300 font-light">
                Receive exclusive updates on new services, special offers, and industry insights from our mobility experts.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-4" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm"
                required
              />
              <button 
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                <i className="ri-mail-send-line mr-2"></i>
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Social Links */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-gray-700">
          <div className="flex items-center mb-6 md:mb-0">
            <span className="text-gray-400 mr-6 font-medium">Follow Us:</span>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="w-12 h-12 bg-gray-800 hover:bg-blue-600 rounded-xl flex items-center justify-center transition-colors duration-200 group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className={`${social.icon} text-gray-300 group-hover:text-white text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-blue-400 transition-colors">Terms of Service</Link>
            <Link to="/cookies" className="hover:text-blue-400 transition-colors">Cookie Policy</Link>
            <Link to="/accessibility" className="hover:text-blue-400 transition-colors">Accessibility</Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-400">
            <div className="mb-4 md:mb-0">
              <span>© {currentYear} Car2Go Car Rental Services. All rights reserved.</span>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-green-400 mr-2"></i>
                <span>ISO 9001:2015 Certified</span>
              </div>
              
              <div className="w-px h-4 bg-gray-600"></div>
              
              <a 
                href="https://readdy.ai/?origin=logo" 
                className="text-gray-500 hover:text-blue-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
